import FormattedMessage from 'I18n/components/FormattedMessage';
import SuiteProductLevelIdentifiersMap from 'pricing-pages-ui/constants/merchCards/SuiteProductLevelIdentifiersMap';
import { hasPricingAndPurchasingSucceededSelector } from 'pricing-pages-ui/selectors/LoadingStateSelectors';
import { useSelector } from 'react-redux';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { SUITE_CARD } from '../../constants/tracking/Locations';
import { useAppSelector } from '../../reducers';
import { ownsCrmSuiteProductLevel, productsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { currentSuiteSkuIdsForProductLevelSelector } from '../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import CurrentPlanTag from '../common/CurrentPlanTag';
import { SectionTitle } from '../common/SharedComponents';
import SuiteFeatureTableBadge from '../common/SuiteFeatureTableBadge';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import IncludedSeatsDisplay from './IncludedSeatsDisplay';
import { MerchCardWrapper, Section } from './MerchCardSharedComponents';
import SuiteTermSelectorContainer from './SuiteTermSelectorContainer';
import SuiteTileCTAsContainer from './SuiteTileCTAsContainer';
import SuiteBusinessObjectives from './business-objectives/SuiteBusinessObjectives';
import { useShowPricePerMonthPerSeat } from './merchCardUtils';
import { CrmSuitePrice } from './price-display/CrmSuitePrice';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getStringKeysForProductLevel = productLevel => {
  switch (productLevel) {
    case FREE:
      return {
        titleKey: 'merchCards.suiteTile.freeCard.title',
        subtitleKey: 'merchCards.suiteTile.freeCard.description'
      };
    case STARTER:
      return {
        titleKey: 'merchCards.suiteTile.starterCard.title',
        subtitleKey: 'merchCards.suiteTile.starterCard.description'
      };
    case PROFESSIONAL:
      return {
        titleKey: 'merchCards.suiteTile.professionalCard.title',
        subtitleKey: 'merchCards.suiteTile.professionalCard.description'
      };
    case ENTERPRISE:
      return {
        titleKey: 'merchCards.suiteTile.enterpriseCard.title',
        subtitleKey: 'merchCards.suiteTile.enterpriseCard.description'
      };
    default:
      return {
        titleKey: '',
        subtitleKey: ''
      };
  }
};
export const SuiteTile = ({
  productLevel,
  commonStyles,
  isFreeToStarter
}) => {
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const ownedProducts = useSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return [];
    return productsFromProductInventorySelector(state);
  });
  const isCurrentPlanLevel = useSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel
  }));
  const skuIds = useAppSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return [];
    return currentSuiteSkuIdsForProductLevelSelector(state, {
      productLevel
    });
  });

  // For showing price/mo/seat and included seats, only show for a bundled suite sku,
  // not the unbundled suite. It adds more logic than it's worth for a small cohort
  // that will eventually go away.
  const suiteSkuId = skuIds && skuIds.length === 1 ? skuIds[0] : null;
  const showPricePerMonthPerSeat = useShowPricePerMonthPerSeat(suiteSkuId);
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    titleKey,
    subtitleKey
  } = getStringKeysForProductLevel(productLevel);
  const isFreePortal = ownedProducts && ownedProducts.length <= 0;
  const showRecommendedBadge = isFreePortal && productLevel === STARTER;
  return /*#__PURE__*/_jsxs(MerchCardWrapper, {
    children: [/*#__PURE__*/_jsxs(Section, {
      "data-selenium-id": `${SuiteProductLevelIdentifiersMap[productLevel]}-merch-card`,
      className: "flex-column p-top-8",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 1,
        position: 'relative'
      }),
      children: [isCurrentPlanLevel && /*#__PURE__*/_jsx(CurrentPlanTag, {}), showRecommendedBadge && /*#__PURE__*/_jsx(SuiteFeatureTableBadge, {
        badgeStyle: {
          top: '8px',
          right: '8px'
        }
      }), /*#__PURE__*/_jsx("div", {
        className: "display-flex align-center",
        children: /*#__PURE__*/_jsx(SectionTitle, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: titleKey
          })
        })
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 2,
        zIndex: 1
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitleKey
      })
    }), /*#__PURE__*/_jsxs(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 3
      }),
      children: [/*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [16, 23, 16],
        widths: [60, 70, 130],
        className: "flex-column",
        Content: CrmSuitePrice,
        contentProps: {
          productLevel,
          useLargePrice: true,
          showPricePerMonthPerSeat
        }
      }), suiteSkuId ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [16, 23, 16],
        widths: [60, 70, 130],
        className: "flex-column",
        Content: IncludedSeatsDisplay,
        contentProps: {
          skuId: skuIds[0],
          queryParamTerm
        }
      }) : null]
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 4
      }),
      children: !isCurrentPlanLevel && /*#__PURE__*/_jsx("div", {
        className: "m-top-3",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !hasPricingAndPurchasingSucceeded,
          heights: [41],
          Content: SuiteTermSelectorContainer,
          contentProps: {
            productLevel,
            onChange: term => setQueryParamTerm(term),
            queryParamTerm,
            trackingSource: SUITE_CARD
          }
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4 p-bottom-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 5
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [47],
        className: "m-bottom-7",
        children: /*#__PURE__*/_jsx(SuiteTileCTAsContainer, {
          productLevel: productLevel,
          trackingSource: SUITE_CARD,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          },
          returnFirstCTAOnly: isFreeToStarter,
          isFreeToStarter: isFreeToStarter,
          hubsSignupProductOverride: SuiteProductLevelIdentifiersMap[productLevel].split('-').join('').toLowerCase()
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 flex-column align-center",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 6
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 flex-column",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 7
      })
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !hasPricingAndPurchasingSucceeded,
      heights: [47],
      className: "m-bottom-7",
      children: /*#__PURE__*/_jsx(SuiteBusinessObjectives, {
        productLevel: productLevel,
        commonStyles: commonStyles
      })
    }), /*#__PURE__*/_jsx(Section, {
      style: Object.assign({}, commonStyles, {
        gridRowStart: 10
      })
    })]
  });
};